import { CandidateBlob, BoundingBox } from '../core/types/tracking';

/**
 * Converts RGB [0..255] to OpenCV HSV [H: 0..180, S: 0..255, V: 0..255]
 */
export function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const rf = r / 255;
  const gf = g / 255;
  const bf = b / 255;

  const max = Math.max(rf, gf, bf);
  const min = Math.min(rf, gf, bf);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rf) {
      h = 60 * (((gf - bf) / delta) % 6);
    } else if (max === gf) {
      h = 60 * ((bf - rf) / delta + 2);
    } else {
      h = 60 * ((rf - gf) / delta + 4);
    }
  }

  if (h < 0) h += 360;

  const hOpenCV = Math.round(h / 2);
  const sOpenCV = Math.round(max === 0 ? 0 : (delta / max) * 255);
  const vOpenCV = Math.round(max * 255);

  return [hOpenCV, sOpenCV, vOpenCV];
}

/**
 * Fast RGB ImageData to binary HSV Mask (1 for in-range, 0 for out-of-range).
 * Supports red hue wrap-around when hLow > hHigh.
 */
export function createHsvMask(
  imageData: ImageData,
  hsvLower: [number, number, number],
  hsvUpper: [number, number, number]
): Uint8Array {
  const { width, height, data } = imageData;
  const mask = new Uint8Array(width * height);

  const [hLow, sLow, vLow] = hsvLower;
  const [hHigh, sHigh, vHigh] = hsvUpper;

  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    const [h, s, v] = rgbToHsv(r, g, b);

    let hMatch = false;
    if (hLow <= hHigh) {
      hMatch = h >= hLow && h <= hHigh;
    } else {
      hMatch = h >= hLow || h <= hHigh;
    }

    if (hMatch && s >= sLow && s <= sHigh && v >= vLow && v <= vHigh) {
      mask[p] = 1;
    } else {
      mask[p] = 0;
    }
  }

  return mask;
}

/**
 * 3x3 Morphological Erosion
 */
export function erode3x3(mask: Uint8Array, width: number, height: number): Uint8Array {
  const output = new Uint8Array(width * height);

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      if (
        mask[idx] &&
        mask[idx - width - 1] && mask[idx - width] && mask[idx - width + 1] &&
        mask[idx - 1] && mask[idx + 1] &&
        mask[idx + width - 1] && mask[idx + width] && mask[idx + width + 1]
      ) {
        output[idx] = 1;
      }
    }
  }

  return output;
}

/**
 * 3x3 Morphological Dilation
 */
export function dilate3x3(mask: Uint8Array, width: number, height: number): Uint8Array {
  const output = new Uint8Array(width * height);

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      if (
        mask[idx] ||
        mask[idx - width - 1] || mask[idx - width] || mask[idx - width + 1] ||
        mask[idx - 1] || mask[idx + 1] ||
        mask[idx + width - 1] || mask[idx + width] || mask[idx + width + 1]
      ) {
        output[idx] = 1;
      }
    }
  }

  return output;
}

/**
 * Morphological Opening (Erosion followed by Dilation) using 3x3 kernel
 */
export function morphologicalOpening(mask: Uint8Array, width: number, height: number): Uint8Array {
  const eroded = erode3x3(mask, width, height);
  return dilate3x3(eroded, width, height);
}

/**
 * Morphological Closing (Dilation followed by Erosion) using 3x3 kernel
 */
export function morphologicalClosing(mask: Uint8Array, width: number, height: number): Uint8Array {
  const dilated = dilate3x3(mask, width, height);
  return erode3x3(dilated, width, height);
}

/**
 * Connected Component Labeling (8-connectivity) and candidate Blob extraction.
 * Uses Euclidean perimeter step weights (1.0 for cardinal, 1.414 for diagonal)
 * to match OpenCV cv2.arcLength perimeter calculation.
 */
export function findCandidateBlobs(
  mask: Uint8Array,
  width: number,
  height: number,
  minArea: number,
  maxArea: number,
  minCircularity: number
): CandidateBlob[] {
  const visited = new Uint8Array(width * height);
  const blobs: CandidateBlob[] = [];
  const SQRT2 = Math.SQRT2; // ~1.4142

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const startIdx = y * width + x;
      if (mask[startIdx] === 1 && visited[startIdx] === 0) {
        // Flood fill BFS
        const queue: number[] = [startIdx];
        visited[startIdx] = 1;

        let sumX = 0;
        let sumY = 0;
        let area = 0;
        let minX = x;
        let maxX = x;
        let minY = y;
        let maxY = y;
        let perimeter = 0;

        let head = 0;
        while (head < queue.length) {
          const curr = queue[head++];
          const cx = curr % width;
          const cy = Math.floor(curr / width);

          area++;
          sumX += cx;
          sumY += cy;

          if (cx < minX) minX = cx;
          if (cx > maxX) maxX = cx;
          if (cy < minY) minY = cy;
          if (cy > maxY) maxY = cy;

          // Check cardinal & diagonal neighbors for accurate Euclidean perimeter calculation
          let cardinalBackground = false;
          let diagonalBackground = false;

          const cardinalNeighbors = [curr - width, curr + width, curr - 1, curr + 1];
          for (const nIdx of cardinalNeighbors) {
            const nx = nIdx % width;
            const ny = Math.floor(nIdx / width);
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              if (mask[nIdx] === 0) cardinalBackground = true;
              else if (visited[nIdx] === 0) {
                visited[nIdx] = 1;
                queue.push(nIdx);
              }
            } else {
              cardinalBackground = true;
            }
          }

          const diagonalNeighbors = [curr - width - 1, curr - width + 1, curr + width - 1, curr + width + 1];
          for (const nIdx of diagonalNeighbors) {
            const nx = nIdx % width;
            const ny = Math.floor(nIdx / width);
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              if (mask[nIdx] === 0) diagonalBackground = true;
              else if (visited[nIdx] === 0) {
                visited[nIdx] = 1;
                queue.push(nIdx);
              }
            } else {
              diagonalBackground = true;
            }
          }

          if (cardinalBackground) {
            perimeter += 1.0;
          } else if (diagonalBackground) {
            perimeter += SQRT2;
          }
        }

        if (area >= minArea && area <= maxArea) {
          const circularity = Math.min(1.0, (4 * Math.PI * area) / Math.max(1, perimeter * perimeter));
          if (circularity >= minCircularity) {
            const centerX = sumX / area;
            const centerY = sumY / area;
            const bbox: BoundingBox = {
              x: minX,
              y: minY,
              width: maxX - minX + 1,
              height: maxY - minY + 1,
            };

            const score = area * circularity;

            blobs.push({
              centerX,
              centerY,
              area,
              perimeter,
              circularity,
              bbox,
              score,
            });
          }
        }
      }
    }
  }

  return blobs;
}

/**
 * Automatically estimates HSV lower and upper bounds from a user-selected ROI.
 * Ensures sLow >= 70 and vLow >= 70 to reject low-saturation furniture/skin/walls.
 */
export function estimateHsvFromRoi(
  imageData: ImageData,
  roi: BoundingBox
): { lower: [number, number, number]; upper: [number, number, number] } {
  const { width, data } = imageData;

  const hList: number[] = [];
  const sList: number[] = [];
  const vList: number[] = [];

  const startX = Math.max(0, Math.floor(roi.x));
  const startY = Math.max(0, Math.floor(roi.y));
  const endX = Math.min(imageData.width, Math.floor(roi.x + roi.width));
  const endY = Math.min(imageData.height, Math.floor(roi.y + roi.height));

  for (let y = startY; y < endY; y++) {
    for (let x = startX; x < endX; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];

      const [h, s, v] = rgbToHsv(r, g, b);
      hList.push(h);
      sList.push(s);
      vList.push(v);
    }
  }

  if (hList.length === 0) {
    return {
      lower: [25, 70, 70],
      upper: [75, 255, 255],
    };
  }

  const calcMeanStd = (arr: number[]) => {
    const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
    const variance = arr.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / arr.length;
    return { mean, std: Math.sqrt(variance) };
  };

  const hStats = calcMeanStd(hList);
  const sStats = calcMeanStd(sList);
  const vStats = calcMeanStd(vList);

  const hTol = Math.max(12, Math.round(hStats.std * 2.8));
  const sTol = Math.max(40, Math.round(sStats.std * 2.8));
  const vTol = Math.max(40, Math.round(vStats.std * 2.8));

  const hLow = Math.max(0, Math.round(hStats.mean - hTol));
  const hHigh = Math.min(180, Math.round(hStats.mean + hTol));

  // Enforce sLow >= 70 and vLow >= 70 to exclude wooden walls/skin/reflections
  const sLow = Math.max(70, Math.round(sStats.mean - sTol));
  const sHigh = Math.min(255, Math.round(sStats.mean + sTol));

  const vLow = Math.max(70, Math.round(vStats.mean - vTol));
  const vHigh = Math.min(255, Math.round(vStats.mean + vTol));

  return {
    lower: [hLow, sLow, vLow],
    upper: [hHigh, sHigh, vHigh],
  };
}
